/**
 * Region detection and currency formatting service
 * 
 * Detects user region using location-based detection only.
 * Region is ENFORCED based on detected location - user preference is NOT used.
 * 
 * Detection priority:
 * 1. localStorage cache (with expiry)
 * 2. Backend API for IP geolocation
 * 3. Browser locale (fallback)
 * 4. Default to US
 * 
 * Note: setRegion() allows manual override, but automatic detection ignores user preference.
 */

import axios from 'axios';
import {
  Region,
  Currency,
  RegionInfo,
  REGION_CONFIG,
  FEATURE_FLAG_KEYS,
  mapCountryCodeToRegion,
  calculateProcessingFee,
  STRIPE_RATE_US,
  STRIPE_RATE_AU_DOMESTIC,
} from '@court-sync/shared';
import { API_BASE_URL } from '../config/api';
import { getFeatureFlags } from './featureFlags';

/**
 * Cache entry structure
 */
interface RegionCacheEntry {
  region: Region;
  source: string;
  detectedAt: number;
}

/**
 * Cache key in localStorage
 */
const CACHE_KEY = 'region_detection_cache';

/**
 * Cache TTL in milliseconds (24 hours)
 */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Region Service
 */
class RegionService {
  private cachedRegion: Region | null = null;

  /**
   * Get cached region from localStorage
   */
  private getCachedRegion(): Region | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) {
        return null;
      }

      const entry: RegionCacheEntry = JSON.parse(cached);
      const now = Date.now();
      const age = now - entry.detectedAt;

      // Check if cache is expired
      if (age > CACHE_TTL_MS) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return entry.region;
    } catch (error) {
      console.warn('Failed to read region cache:', error);
      return null;
    }
  }

  /**
   * Set cached region in localStorage
   */
  private setCachedRegion(region: Region, source: string): void {
    try {
      const entry: RegionCacheEntry = {
        region,
        source,
        detectedAt: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
      this.cachedRegion = region;
    } catch (error) {
      console.warn('Failed to cache region:', error);
    }
  }

  /**
   * Detect region from backend API
   * 
   * Backend returns the actual country code detected.
   * Frontend maps it to a region: AU → AU (AUD products), everything else → US (USD products)
   */
  private async detectFromBackendAPI(): Promise<Region | null> {
    try {
      const response = await axios.get<{
        success: boolean;
        data: {
          countryCode: string;
          region: Region;
          source: string;
          confidence: string;
        };
      }>(`${API_BASE_URL}/region/detect`, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000, // 5 second timeout
      });

      if (response.data.success && response.data.data?.countryCode) {
        // Map country code to an active market. GB remains US unless the market flag is enabled.
        const countryCode = response.data.data.countryCode;
        const featureFlags = await getFeatureFlags().catch(() => ({}));
        const mappedRegion = mapCountryCodeToRegion(countryCode, {
          [FEATURE_FLAG_KEYS.GB_MARKET_EXPANSION]:
            featureFlags[FEATURE_FLAG_KEYS.GB_MARKET_EXPANSION] === true,
        });
        
        console.debug('Region mapped from country code:', {
          countryCode,
          mappedRegion,
          source: response.data.data.source,
          confidence: response.data.data.confidence,
        });
        
        return mappedRegion;
      }

      return null;
    } catch (error: any) {
      // Log but don't throw - we'll fall back to other methods
      console.debug('Backend region detection failed:', {
        error: error.message,
      });
      return null;
    }
  }

  /**
   * Detect region from browser locale (fallback when API is unavailable)
   */
  private detectFromBrowserLocale(): Region {
    // First, try to detect from timezone (more reliable than locale)
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone?.toLowerCase() || '';
      
      // Check for Australian timezones
      if (timezone.includes('australia') || timezone.includes('sydney') || 
          timezone.includes('melbourne') || timezone.includes('brisbane') || 
          timezone.includes('perth') || timezone.includes('adelaide') ||
          timezone.includes('darwin') || timezone.includes('hobart')) {
        console.debug('Detected AU from timezone:', timezone);
        return 'AU';
      }
      
      // Check for US timezones
      if (timezone.includes('america') || timezone.includes('new_york') || 
          timezone.includes('los_angeles') || timezone.includes('chicago') || 
          timezone.includes('denver') || timezone.includes('phoenix')) {
        console.debug('Detected US from timezone:', timezone);
        return 'US';
      }
    } catch (error) {
      console.debug('Timezone detection failed, falling back to locale:', error);
    }
    
    // Fall back to browser locale
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const localeLower = locale.toLowerCase();
    
    // Check for Australian locale first (more specific)
    if (localeLower.includes('au') || localeLower.includes('australia') || localeLower.includes('en-au')) {
      console.debug('Detected AU from locale:', locale);
      return 'AU';
    }
    
    // Check for US locale
    if (localeLower.includes('us') || localeLower.includes('en-us')) {
      console.debug('Detected US from locale:', locale);
      return 'US';
    }
    
    // Default to US when IP geolocation fails (matches backend /region/detect default)
    return 'US';
  }

  /**
   * Detect user's region
   * 
   * Detection priority (location-based enforcement):
   * 1. localStorage cache (with expiry) - on localhost we clear first so API is used
   * 2. Backend API for IP geolocation (same as /region/detect)
   * 3. Browser locale/timezone detection (fallback)
   * 4. Default to US (matches backend when detection fails)
   * 
   * Note: User preference is NOT used - region is enforced based on detected location
   */
  async detectRegion(): Promise<Region> {
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1' ||
       process.env.NODE_ENV === 'development');

    // On localhost, clear cache so we use the same API result as /region/detect
    // (avoids showing AU plans when the API returns US)
    if (isLocalhost) {
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch (error) {
        console.debug('Failed to clear region cache:', error);
      }
    }

    // 1. Check cache first (empty on localhost after clear above)
    const cached = this.getCachedRegion();
    if (cached) {
      this.cachedRegion = cached;
      return cached;
    }

    // 2. Try backend API for IP geolocation
    try {
      const apiRegion = await this.detectFromBackendAPI(); // Location-based only
      if (apiRegion) {
        this.setCachedRegion(apiRegion, 'api');
        return apiRegion;
      }
    } catch (error) {
      // Continue to fallback methods
      console.debug('Region detection from API failed, using fallback:', error);
    }

    // 3. Fall back to browser locale
    const localeRegion = this.detectFromBrowserLocale();
    this.setCachedRegion(localeRegion, 'browser_locale');
    return localeRegion;
  }

  /**
   * Set user's preferred region
   */
  setRegion(region: Region): void {
    localStorage.setItem('user_region', region);
    this.setCachedRegion(region, 'manual');
    this.cachedRegion = region;
  }

  /**
   * Get current region
   */
  getRegion(): Region | null {
    if (this.cachedRegion) {
      return this.cachedRegion;
    }

    // Check localStorage
    const stored = localStorage.getItem('user_region');
    if (stored && REGION_CONFIG[stored as Region]) {
      this.cachedRegion = stored as Region;
      return stored as Region;
    }

    // Check cache
    const cached = this.getCachedRegion();
    if (cached) {
      this.cachedRegion = cached;
      return cached;
    }

    return null;
  }

  /**
   * Get currency info for a region
   */
  getCurrencyInfo(region: Region): RegionInfo {
    return REGION_CONFIG[region];
  }

  /**
   * Format price based on region
   */
  formatPrice(amount: number, region: Region): string {
    const config = REGION_CONFIG[region];
    return `${config.symbol}${amount.toFixed(2)}`;
  }

  /**
   * Format price with currency symbol
   */
  formatPriceWithSymbol(amount: number, currency: Currency): string {
    const market = Object.values(REGION_CONFIG).find((config) => config.currency === currency);
    return `${market?.symbol ?? '$'}${amount.toFixed(2)}`;
  }

  /**
   * Get currency symbol for a region
   */
  getCurrencySymbol(region: Region): string {
    return REGION_CONFIG[region].symbol;
  }

  /**
   * Clear cached region (useful for debugging/testing)
   */
  clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('user_region');
      this.cachedRegion = null;
      console.debug('Region cache cleared');
    } catch (error) {
      console.warn('Failed to clear region cache:', error);
    }
  }

  /**
   * Calculate fee breakdown using shared processing-fee logic (same as backend and lesson payment forms).
   * Ensures academy receives exactly the subtotal after Stripe takes its fee from the total.
   */
  calculateFeeBreakdown(amount: number, region: Region): {
    subtotal: number;
    fee: number;
    total: number;
    feePercentage: number;
    fixedFee: number;
    currency: Currency;
  } {
    const fee = calculateProcessingFee(amount, region);
    const total = Math.round((amount + fee) * 100) / 100;
    const rate = region === 'AU' ? STRIPE_RATE_AU_DOMESTIC : STRIPE_RATE_US;
    return {
      subtotal: amount,
      fee,
      total,
      feePercentage: rate.percentage * 100,
      fixedFee: rate.fixed,
      currency: REGION_CONFIG[region].currency,
    };
  }
}

export const regionService = new RegionService();
export default regionService;

// Export types for backward compatibility
export type { Region, Currency };
